import { useCallback } from 'react';
import { useLocation, useNavigate, useSearch } from '@tanstack/react-router';

import { NavigationBar } from '@/components/NavigationBar';
import StepperCard from '@/components/common/StepperCard.tsx';
import { useSessionSave } from '@/hooks/use-session-save';
import { useSessionStore } from '@/hooks/use-session-store/use-session-store';
import { getState } from '@/lib/zipcode.ts';
import {
  answerFamily,
  getPostFamilyResolutionDestination,
  getResolveBackDestination,
  getResolveOutcome,
  getReviewInsurancesForFamily,
  getVerifiedInsuranceForFamily,
  markFamilyUnprovided,
  markLaterFamiliesUnresolved,
  queueFamilyInsurance,
  resolveFamily,
  setVerifiedFamilyInsurances,
} from '@/types/insurance-builder-eligibility-revamp.ts';
import {
  CARRIER_TYPE,
  PLAN_FAMILY,
  type PlanFamily,
  type QueuedInsurance,
  type UnprovidedInsurance,
  type VerifiedInsurance,
  carrierTypeToFamily,
  planFamilyToCarrierTypes,
  prettyFamilyName,
  prettyTypeName,
} from '@/types/insurance.type.ts';
import { AskAllForm } from '@/views/insurance/flows/eligibility-revamp/components/AskAllForm.tsx';
import { AskManagedForm } from '@/views/insurance/flows/eligibility-revamp/components/AskManagedForm.tsx';
import { getFoundBaseCoverageLabel } from '@/views/insurance/flows/eligibility-revamp/components/FoundCoverageBanner.tsx';
import { ResolveConfirmManaged } from '@/views/insurance/flows/eligibility-revamp/components/ResolveConfirmManaged.tsx';

export function InsuranceResolve() {
  const navigate = useNavigate();
  const { saveSession } = useSessionSave();
  const {
    planFamily,
    override,
    preserveLaterFamilies,
    previousPlanFamily,
    previousUploadPlanType,
  } = useSearch({ from: '/appLayout/wizardLayout/insurance/resolve' });
  const showError = useLocation({
    select: (location) => location.state.showVerificationError === true,
  });
  const { insuranceBuilder: builder, insuranceZipCode } = useSessionStore();

  const currentFamily = planFamily || PLAN_FAMILY.MEDICARE;
  const reviewInsurances = getReviewInsurancesForFamily(builder, currentFamily);
  const baseOutcome = getResolveOutcome(reviewInsurances, currentFamily);
  const foundInsurance = getVerifiedInsuranceForFamily(
    { ...builder, verifiedInsurances: reviewInsurances },
    currentFamily
  );
  const { base, managed } = planFamilyToCarrierTypes(currentFamily);
  const state = getState(insuranceZipCode) ?? undefined;
  const discoveredBaseInsurance = reviewInsurances.find(
    (insurance) => insurance.resolveSource === 'discovery' && insurance.carrier.carrierType === base
  );
  const hasDiscoveredBaseInsurance = Boolean(discoveredBaseInsurance);
  const hasDiscoveredManagedInsurance = reviewInsurances.some(
    (insurance) =>
      insurance.resolveSource === 'discovery' && insurance.carrier.carrierType === managed
  );
  const showDiscoveredBaseContext = hasDiscoveredBaseInsurance && !hasDiscoveredManagedInsurance;
  const medicaidFoundBeforeMedicareLabel = getMedicaidDiscoveryBannerForMedicareQuestion(
    builder.discoveredInsurances,
    currentFamily,
    state
  );
  const lastSubmittedInsurance = builder.unverifiedInsurances.find((insurance) =>
    [base, managed].includes(insurance.carrier.carrierType)
  );

  const outcome = override ?? baseOutcome;
  const isPreservingLaterFamiliesFromBenefits = preserveLaterFamilies === 'true';
  const shouldReviewLaterFamilies =
    builder.resolvedFamilies.includes(currentFamily) && !isPreservingLaterFamiliesFromBenefits;

  const applyFamilyEdit = useCallback(
    (updatedBuilder: typeof builder) => {
      if (!shouldReviewLaterFamilies) return updatedBuilder;
      return markLaterFamiliesUnresolved(updatedBuilder, currentFamily);
    },
    [currentFamily, shouldReviewLaterFamilies]
  );

  const navigateToNext = useCallback(() => {
    const updatedBuilder = useSessionStore.getState().insuranceBuilder;
    const destination = getPostFamilyResolutionDestination(updatedBuilder, currentFamily);

    if (destination.type === 'resolve') {
      navigate({
        to: '/insurance/resolve',
        search: {
          planFamily: destination.planFamily,
          previousPlanFamily: destination.previousPlanFamily,
          ...(isPreservingLaterFamiliesFromBenefits && { preserveLaterFamilies }),
        },
      });
      return;
    }

    if (destination.type === 'benefits') {
      navigate({ to: '/insurance/benefits' });
      return;
    }

    navigate({
      to: '/insurance/ineligible',
      search: { reason: destination.reason },
      replace: true,
    });
  }, [currentFamily, navigate, preserveLaterFamilies, isPreservingLaterFamiliesFromBenefits]);

  const goBack = () => {
    if (outcome === 'ask_managed' && baseOutcome === 'confirm_managed') {
      navigate({
        to: '/insurance/resolve',
        search: {
          planFamily: currentFamily,
          previousPlanFamily,
          previousUploadPlanType,
          ...(isPreservingLaterFamiliesFromBenefits && { preserveLaterFamilies }),
        },
        replace: true,
      });
      return;
    }

    const destination = getResolveBackDestination({
      previousPlanFamily,
      previousUploadPlanType,
    });

    if (destination.type === 'upload') {
      navigate({
        to: '/insurance/upload',
        search: {
          planType: destination.planType,
          override,
          previousPlanFamily,
          ...(isPreservingLaterFamiliesFromBenefits && { preserveLaterFamilies }),
        },
        replace: true,
      });
      return;
    }

    if (destination.type === 'resolve') {
      navigate({
        to: '/insurance/resolve',
        search: {
          planFamily: destination.planFamily,
          ...(destination.override && { override: destination.override }),
          ...(isPreservingLaterFamiliesFromBenefits && { preserveLaterFamilies }),
        },
        replace: true,
      });
      return;
    }

    navigate({ to: '/1', replace: true });
  };

  const handleNoCoverage = useCallback(() => {
    const { updateState } = useSessionStore.getState();
    const updatedBuilder = applyFamilyEdit(answerFamily(builder, currentFamily, false));
    updateState({ insuranceBuilder: updatedBuilder });
    saveSession();
    navigateToNext();
  }, [applyFamilyEdit, builder, currentFamily, saveSession, navigateToNext]);

  const handleConfirmBase = useCallback(() => {
    const { updateState } = useSessionStore.getState();
    const updatedBuilder = applyFamilyEdit(
      discoveredBaseInsurance
        ? resolveFamily(
            setVerifiedFamilyInsurances(builder, discoveredBaseInsurance),
            currentFamily,
            true
          )
        : answerFamily(builder, currentFamily, true)
    );
    updateState({ insuranceBuilder: updatedBuilder });
    saveSession();
    navigateToNext();
  }, [
    applyFamilyEdit,
    builder,
    currentFamily,
    discoveredBaseInsurance,
    saveSession,
    navigateToNext,
  ]);

  const handleSubmitInsurance = useCallback(
    (queued: QueuedInsurance) => {
      const updatedBuilder = applyFamilyEdit(queueFamilyInsurance(builder, queued));
      useSessionStore.getState().updateState({ insuranceBuilder: updatedBuilder });
      saveSession();
      navigate({
        to: '/insurance/verifying',
        search: {
          override,
          previousPlanFamily,
          previousUploadPlanType,
          ...(isPreservingLaterFamiliesFromBenefits && { preserveLaterFamilies }),
        },
        replace: true,
      });
    },
    [
      applyFamilyEdit,
      builder,
      navigate,
      override,
      preserveLaterFamilies,
      previousPlanFamily,
      previousUploadPlanType,
      saveSession,
      isPreservingLaterFamiliesFromBenefits,
    ]
  );

  const handleCannotProvide = useCallback(
    (insurance: UnprovidedInsurance) => {
      const updatedBuilder = applyFamilyEdit(markFamilyUnprovided(builder, insurance));
      useSessionStore.getState().updateState({ insuranceBuilder: updatedBuilder });
      saveSession();
      navigate({
        to: '/insurance/send-link',
        search: {
          planFamily: currentFamily,
          override,
          previousPlanFamily,
          previousUploadPlanType,
          ...(isPreservingLaterFamiliesFromBenefits && { preserveLaterFamilies }),
        },
      });
    },
    [
      applyFamilyEdit,
      builder,
      currentFamily,
      navigate,
      override,
      preserveLaterFamilies,
      previousPlanFamily,
      previousUploadPlanType,
      saveSession,
      isPreservingLaterFamiliesFromBenefits,
    ]
  );

  const handleConfirmManaged = useCallback(() => {
    if (!foundInsurance) return;

    const { updateState } = useSessionStore.getState();
    const updatedBuilder = applyFamilyEdit(
      resolveFamily(setVerifiedFamilyInsurances(builder, foundInsurance), currentFamily, true)
    );
    updateState({ insuranceBuilder: updatedBuilder });
    saveSession();
    navigateToNext();
  }, [foundInsurance, applyFamilyEdit, builder, currentFamily, saveSession, navigateToNext]);

  const handleRejectConfirmation = useCallback(() => {
    navigate({
      to: '/insurance/resolve',
      search: {
        planFamily: currentFamily,
        override: 'ask_managed',
        previousPlanFamily,
        previousUploadPlanType,
        ...(isPreservingLaterFamiliesFromBenefits && { preserveLaterFamilies }),
      },
    });
  }, [
    navigate,
    currentFamily,
    preserveLaterFamilies,
    previousPlanFamily,
    previousUploadPlanType,
    isPreservingLaterFamiliesFromBenefits,
  ]);

  return (
    <section>
      <NavigationBar onBack={goBack} backLabel="Back" stepIndicator="2 of 3" />
      <StepperCard currentStep={2} totalSteps={3}>
        {outcome === 'ask_all' && (
          <AskAllForm
            key={currentFamily}
            family={currentFamily}
            state={state}
            foundCoverageLabel={medicaidFoundBeforeMedicareLabel}
            showError={showError}
            initialInsurance={lastSubmittedInsurance}
            onNoCoverage={handleNoCoverage}
            onSubmit={handleSubmitInsurance}
            onCannotProvide={handleCannotProvide}
          />
        )}

        {outcome === 'ask_managed' && (
          <AskManagedForm
            key={currentFamily}
            family={currentFamily}
            state={state}
            hasDiscoveredBaseInsurance={hasDiscoveredBaseInsurance}
            showDiscoveredBaseContext={showDiscoveredBaseContext}
            showError={showError}
            initialInsurance={lastSubmittedInsurance}
            onConfirmBase={handleConfirmBase}
            onSubmit={handleSubmitInsurance}
            onCannotProvide={handleCannotProvide}
          />
        )}

        {outcome === 'confirm_managed' && foundInsurance && (
          <ResolveConfirmManaged
            key={currentFamily}
            family={currentFamily}
            familyName={prettyFamilyName(currentFamily)}
            managedName={prettyTypeName(managed)}
            insurance={foundInsurance}
            showError={showError}
            onConfirm={handleConfirmManaged}
            onReject={handleRejectConfirmation}
          />
        )}
      </StepperCard>
    </section>
  );
}

function getMedicaidDiscoveryBannerForMedicareQuestion(
  discoveredInsurances: VerifiedInsurance[],
  currentFamily: PlanFamily,
  state: ReturnType<typeof getState> | undefined
): string | undefined {
  if (currentFamily !== PLAN_FAMILY.MEDICARE) return undefined;

  const hasDiscoveredMedicare = discoveredInsurances.some(
    (insurance) => carrierTypeToFamily(insurance.carrier.carrierType) === PLAN_FAMILY.MEDICARE
  );
  if (hasDiscoveredMedicare) return undefined;

  const discoveredMedicaid =
    discoveredInsurances.find(
      (insurance) => insurance.carrier.carrierType === CARRIER_TYPE.MEDICAID_MANAGED_CARE
    ) ??
    discoveredInsurances.find(
      (insurance) => insurance.carrier.carrierType === CARRIER_TYPE.MEDICAID
    );
  if (!discoveredMedicaid) return undefined;

  const coverageState = state ?? discoveredMedicaid.carrier.state;

  if (discoveredMedicaid.carrier.carrierType === CARRIER_TYPE.MEDICAID_MANAGED_CARE) {
    return 'We found your active managed Medicaid plan';
  }

  return getFoundBaseCoverageLabel(PLAN_FAMILY.MEDICAID, coverageState);
}
